/**
 * Operationele P0 — Call Log / Telefoonregistratie sectie.
 *
 * Compact balie-formulier: < 30 sec van klant-aan-de-lijn tot opgeslagen.
 * Inline "Reminder aanmaken"-checkbox die in 1 submit zowel de call-log
 * als de gekoppelde reminder creëert.
 *
 * Filters: vandaag / week / open / urgent / categorie / Pickup-koppeling.
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  PhoneIncoming, Plus, Search, AlertCircle, CheckCircle2, Clock, ChevronRight, Link as LinkIcon,
  CalendarClock, X as XIcon, Loader2,
} from "lucide-react";

import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { Checkbox } from "../../ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../ui/select";

import DatePickerField from "../DatePickerField";
import { adminApi, formatApiError } from "../../../context/AuthContext";
// P0 SPRINT (Aug 2026) — CANONICAL CUSTOMER AUTOFILL
import CustomerAutofillPicker from "../../shared/CustomerAutofillPicker";
import ContactPersonSelector from "../../shared/ContactPersonSelector";
import EnterpriseEntityPicker from "../shared/EnterpriseEntityPicker";

// Sprint jun 2026 — koppelen gebeurt via menselijke zoek-en-selecteer, nooit
// meer door een technisch Record ID te plakken. Per koppeltype de bijbehorende
// entity-search adapter.
const LINK_TYPE_LABELS = {
  pickup: "Pickup & Delivery",
  klant: "Klant",
  zending: "Zending",
  offerte: "Offerte",
  factuur: "Factuur",
  webshop_order: "Webshoporder",
};
const LINK_ENTITY_TYPES = {
  pickup: "pickup_delivery",
  klant: "customer",
  zending: "shipment",
  offerte: "quote",
  factuur: "invoice",
};

const CATEGORY_LABELS = {
  pickup_aanvraag: "Pickup aanvraag",
  delivery_aanvraag: "Delivery aanvraag",
  betaling: "Betaling",
  tracking_status: "Tracking / status",
  klacht: "Klacht",
  schade: "Schade",
  offerte: "Offerte",
  webshop_order: "Webshoporder",
  douane_paklijst: "Douane / paklijst",
  factuur: "Factuur",
  import: "Import",
  shop_drop_ship: "ShopDrop&Ship",
  algemene_vraag: "Algemene vraag",
};
const STATUS_LABELS = {
  open: "Open",
  in_behandeling: "In behandeling",
  wacht_klant: "Wacht op klant",
  wacht_intern: "Wacht op interne actie",
  afgerond: "Afgerond",
  geannuleerd: "Geannuleerd",
};
const PRIO_COLOR = {
  urgent: "bg-red-100 text-red-800 border-red-200",
  hoog: "bg-orange-100 text-orange-800 border-orange-200",
  normaal: "bg-gray-100 text-gray-700 border-gray-200",
  laag: "bg-blue-50 text-blue-700 border-blue-200",
};

const EMPTY_FORM = {
  customer_id: null,   // P0 SPRINT — link naar canonical customer entity
  customer_number: "", // P0 SPRINT — klantnummer voor display
  customer_name: "",
  phone: "",
  mobile: "",          // P0 SPRINT — mobiel apart van telefoon
  email: "",
  company_name: "",
  // Adresvelden voor context bij pickup-aanvraag / delivery-aanvraag
  street: "",
  house_number: "",
  house_number_addition: "",
  postal_code: "",
  city: "",
  province: "",
  country: "",
  subject: "",
  category: "pickup_aanvraag",
  summary: "",
  action_required: false,
  priority: "normaal",
  status: "open",
  link_type: "",
  link_id: "",
  link_label: "",
  internal_note: "",
  add_reminder: false,
  reminder_date: "",
  reminder_time: "",
  reminder_priority: "normaal",
  reminder_text: "",
};

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}
function tomorrowIso() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

export default function CallLogSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("today");
  const [statusF, setStatusF] = useState("");
  const [prio, setPrio] = useState("");
  const [cat, setCat] = useState("");
  const [linkedPickup, setLinkedPickup] = useState(false);
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState(null);

  const [reloadTick, setReloadTick] = useState(0);
  const triggerReload = () => setReloadTick((n) => n + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = { limit: 200 };
        if (range && range !== "all") params.range = range;
        if (statusF) params.status = statusF;
        if (prio) params.priority = prio;
        if (cat) params.category = cat;
        if (linkedPickup) params.linked_pickup = 1;
        if (q.trim()) params.q = q.trim();
        const r = await adminApi.get("/admin/call-logs", { params });
        if (!cancelled) setItems(r.data || []);
      } catch (err) {
        if (!cancelled) toast.error(err?.response?.data?.detail || "Laden mislukt");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [range, statusF, prio, cat, linkedPickup, reloadTick]);

  const reload = triggerReload;

  const urgentCount = useMemo(() => items.filter((i) => i.priority === "urgent" && i.status !== "afgerond" && i.status !== "geannuleerd").length, [items]);

  return (
    <div className="space-y-4" data-testid="call-log-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-chivo font-black text-2xl text-[#082c50] flex items-center gap-2">
            <PhoneIncoming className="w-6 h-6" /> Telefoonregistratie
            {urgentCount > 0 && (
              <span className="bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full" data-testid="call-log-urgent-badge">{urgentCount} URGENT</span>
            )}
          </h2>
          <p className="text-xs text-gray-600">Binnenkomende gesprekken digitaal vastleggen — geen pen en papier meer.</p>
        </div>
        <Button onClick={() => setCreating(true)} className="bg-[#082c50] hover:bg-[#06223f] text-white rounded-xl text-xs font-chivo font-bold uppercase" data-testid="call-log-new">
          <Plus className="w-3 h-3 mr-1" /> Nieuwe registratie
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 p-3 flex flex-wrap items-center gap-2">
        <div className="flex gap-1 border border-gray-200">
          {[["today","Vandaag"],["this_week","Deze week"],["all","Alle"]].map(([k,l]) => (
            <button key={k} onClick={() => setRange(k)}
              className={`px-3 py-1.5 text-xs uppercase font-bold ${range === k ? "bg-[#082c50] text-white" : "text-gray-600 hover:bg-gray-50"}`}
              data-testid={`call-log-range-${k}`}>{l}</button>
          ))}
        </div>
        <Select value={statusF || "all"} onValueChange={(v) => setStatusF(v === "all" ? "" : v)}>
          <SelectTrigger className="w-44 rounded-none text-xs h-9" data-testid="call-log-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle statussen</SelectItem>
            {Object.entries(STATUS_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={prio || "all"} onValueChange={(v) => setPrio(v === "all" ? "" : v)}>
          <SelectTrigger className="w-32 rounded-none text-xs h-9" data-testid="call-log-filter-priority"><SelectValue placeholder="Prio" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle prioriteit</SelectItem>
            <SelectItem value="urgent">Urgent</SelectItem>
            <SelectItem value="hoog">Hoog</SelectItem>
            <SelectItem value="normaal">Normaal</SelectItem>
            <SelectItem value="laag">Laag</SelectItem>
          </SelectContent>
        </Select>
        <Select value={cat || "all"} onValueChange={(v) => setCat(v === "all" ? "" : v)}>
          <SelectTrigger className="w-44 rounded-none text-xs h-9" data-testid="call-log-filter-category"><SelectValue placeholder="Categorie" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle categorieën</SelectItem>
            {Object.entries(CATEGORY_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-1 text-xs cursor-pointer" data-testid="call-log-filter-linked-pickup-label">
          <Checkbox checked={linkedPickup} onCheckedChange={(v) => setLinkedPickup(Boolean(v))} data-testid="call-log-filter-linked-pickup" />
          Gekoppeld aan Pickup & Delivery
        </label>
        <div className="flex-1 min-w-[200px] relative">
          <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && reload()}
            placeholder="Zoek klant/telefoon/onderwerp..." className="pl-7 rounded-none text-xs h-9" data-testid="call-log-search" />
        </div>
      </div>

      {/* List */}
      <div className="bg-white border border-gray-200">
        {loading ? (
          <div className="p-6 text-gray-500 text-sm">Laden...</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-gray-500 text-sm" data-testid="call-log-empty">
            Geen registraties voor dit filter. Klik op &quot;Nieuwe registratie&quot; om er een toe te voegen.
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {items.map((c) => (
              <li key={c.id} className="p-3 hover:bg-gray-50 cursor-pointer flex items-start gap-3" onClick={() => setSelected(c)} data-testid={`call-log-row-${c.id}`}>
                <div className={`text-[10px] uppercase font-bold px-2 py-0.5 border ${PRIO_COLOR[c.priority] || PRIO_COLOR.normaal} shrink-0`}>{c.priority}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-bold text-sm">{c.customer_name}</span>
                    {c.phone && <span className="text-xs text-gray-500">{c.phone}</span>}
                    {c.link_type && (
                      <span className="text-[10px] uppercase font-bold text-[#1a5193] bg-blue-50 border border-blue-200 px-1.5 py-0.5 inline-flex items-center gap-1 max-w-full"
                            data-testid={`call-log-link-badge-${c.id}`}>
                        <LinkIcon className="w-2.5 h-2.5 shrink-0" />
                        <span className="truncate normal-case font-medium">
                          {c.link_label || LINK_TYPE_LABELS[c.link_type] || c.link_type}
                        </span>
                      </span>
                    )}
                    {c.reminder_id && (
                      <span className="text-[10px] uppercase font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 flex items-center gap-1">
                        <Clock className="w-2.5 h-2.5" /> Reminder
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-700 mt-0.5">{c.subject}</div>
                  <div className="text-xs text-gray-500 mt-1 flex items-center gap-2 flex-wrap">
                    <span>{CATEGORY_LABELS[c.category] || c.category}</span>
                    <span>·</span>
                    <span>{new Date(c.created_at).toLocaleString("nl-NL")}</span>
                    <span>·</span>
                    <span>{STATUS_LABELS[c.status] || c.status}</span>
                    {c.action_required && <span className="text-amber-700 font-bold">⚡ Actie nodig</span>}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
              </li>
            ))}
          </ul>
        )}
      </div>

      {creating && <CallLogDialog onClose={() => setCreating(false)} onSaved={reload} />}
      {selected && <CallLogDetailDialog cl={selected} onClose={() => setSelected(null)} onUpdated={reload} />}
    </div>
  );
}

// ============================================================
// Create dialog
// ============================================================
function CallLogDialog({ onClose, onSaved, prefill = {} }) {
  const [form, setForm] = useState({ ...EMPTY_FORM, ...prefill });
  const [submitting, setSubmitting] = useState(false);
  // Batch 3 · Fase 5 — contactpersoon selectie (primary of contact-{id}) +
  // sourceCustomer voor contact-options + stale-reset.
  const [sourceCustomer, setSourceCustomer] = useState(null);
  const [contactKey, setContactKey] = useState("primary");
  const setF = (k, v) => setForm((cur) => ({ ...cur, [k]: v }));

  const submit = async () => {
    if (!form.customer_name.trim()) return toast.error("Klantnaam is verplicht");
    if (!form.subject.trim()) return toast.error("Onderwerp is verplicht");
    if (form.add_reminder && !form.reminder_date) {
      return toast.error("Datum voor reminder is verplicht");
    }
    setSubmitting(true);
    try {
      const payload = {
        customer_id: form.customer_id || null,
        customer_number: form.customer_number || null,
        customer_name: form.customer_name.trim(),
        phone: form.phone.trim() || null,
        mobile: form.mobile.trim() || null,
        email: form.email.trim().toLowerCase() || null,
        company_name: form.company_name.trim() || null,
        // Optionele adres-context (mag null zijn — backend accepteert al bekende velden)
        street: form.street.trim() || null,
        house_number: form.house_number.trim() || null,
        house_number_addition: form.house_number_addition.trim() || null,
        postal_code: form.postal_code.trim() || null,
        city: form.city.trim() || null,
        province: form.province.trim() || null,
        country: form.country.trim() || null,
        subject: form.subject.trim(),
        category: form.category,
        summary: form.summary.trim() || null,
        action_required: form.action_required,
        priority: form.priority,
        status: form.status,
        link_type: form.link_type || null,
        link_id: form.link_id.trim() || null,
        internal_note: form.internal_note.trim() || null,
      };
      if (form.add_reminder) {
        payload.reminder = {
          title: null,  // server build default "Opvolging telefoonnotitie..."
          text: form.reminder_text.trim() || null,
          date: form.reminder_date,
          time_of_day: form.reminder_time || null,
          priority: form.reminder_priority,
        };
      }
      await adminApi.post("/admin/call-logs", payload);
      toast.success(form.add_reminder ? "Registratie + reminder opgeslagen" : "Registratie opgeslagen");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Opslaan mislukt");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-xl max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="call-log-dialog">
        <DialogHeader>
          <DialogTitle className="font-chivo font-black text-2xl text-[#082c50]">Nieuwe telefoonregistratie</DialogTitle>
          <DialogDescription>Vul snel de basisgegevens in. Onderwerp en klantnaam zijn verplicht; meer mag.</DialogDescription>
        </DialogHeader>

        {/* P0 SPRINT (Aug 2026) — Canonical klant-picker bovenaan.
            Zoek op klantnummer/naam/bedrijf/telefoon/mobiel/whatsapp/e-mail/postcode/plaats.
            Na selectie wordt alle contact- en adresdata direct ingevuld. */}
        <div className="mb-4" data-testid="call-log-customer-picker-wrapper">
          <CustomerAutofillPicker
            testIdPrefix="call-log-customer-picker"
            label="Bestaande klant zoeken"
            placeholder="Zoek op naam, klantnummer, telefoon, e-mail, postcode…"
            autoFocus
            showRecent
            hint={form.customer_id
              ? `Gekoppeld aan klant ${form.customer_number || form.customer_name} — de medewerker hoeft alleen het gesprek te registreren.`
              : "Optioneel — als de beller een bestaande klant is, worden alle contactgegevens automatisch ingevuld."}
            onSelect={(c) => {
              if (!c) return;
              setSourceCustomer(c);
              // Batch 3 · Fase 5 — stale-reset: bij elke nieuwe klant terug
              // naar primair contact.
              setContactKey("primary");
              setForm((cur) => ({
                ...cur,
                customer_id: c.customer_id || c.id || null,
                customer_number: c.customer_number || "",
                customer_name: c.display_name
                  || [c.first_name, c.last_name].filter(Boolean).join(" ").trim()
                  || c.company_name
                  || cur.customer_name,
                phone: c.phone || cur.phone,
                mobile: c.mobile || cur.mobile,
                email: c.email || cur.email,
                company_name: c.company_name || cur.company_name,
                street: c.street || cur.street,
                house_number: c.house_number || cur.house_number,
                house_number_addition: c.house_number_addition || cur.house_number_addition,
                postal_code: c.postal_code || cur.postal_code,
                city: c.city || cur.city,
                province: c.province || cur.province,
                country: c.country || cur.country,
              }));
              toast.success("Klantgegevens automatisch ingevuld");
            }}
          />
          {/* Batch 3 · Fase 5 — Contact-selector verschijnt alléén als de
              gekozen klant extra contactpersonen heeft. customer_id blijft
              leidend; enkel naam/email/telefoon in het formulier worden
              gepatcht. */}
          <ContactPersonSelector
            customer={sourceCustomer}
            value={contactKey}
            onSelect={(key, contact) => {
              setContactKey(key);
              setForm((cur) => ({
                ...cur,
                customer_name: contact.name || cur.customer_name,
                email: contact.email || cur.email,
                phone: contact.phone || cur.phone,
              }));
            }}
            testIdPrefix="call-log-contact"
          />
        </div>

        {/* Klant + onderwerp */}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Klantnaam *" testId="call-log-customer-name" value={form.customer_name} onChange={(v) => setF("customer_name", v)} />
          <Field label="Telefoon" testId="call-log-phone" value={form.phone} onChange={(v) => setF("phone", v)} />
          <Field label="Mobiel" testId="call-log-mobile" value={form.mobile} onChange={(v) => setF("mobile", v)} />
          <Field label="E-mail" testId="call-log-email" value={form.email} onChange={(v) => setF("email", v)} />
          <Field label="Bedrijf" testId="call-log-company" value={form.company_name} onChange={(v) => setF("company_name", v)} />
          <Field label="Klantnummer" testId="call-log-customer-number" value={form.customer_number} onChange={(v) => setF("customer_number", v)} />
          <div className="col-span-2">
            <Field label="Onderwerp *" testId="call-log-subject" value={form.subject} onChange={(v) => setF("subject", v)} />
          </div>
        </div>

        {/* Categorie + status + prio */}
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-[11px] uppercase font-bold">Categorie *</Label>
            <Select value={form.category} onValueChange={(v) => setF("category", v)}>
              <SelectTrigger data-testid="call-log-category" className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(CATEGORY_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase font-bold">Prioriteit</Label>
            <Select value={form.priority} onValueChange={(v) => setF("priority", v)}>
              <SelectTrigger data-testid="call-log-priority" className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="urgent">Urgent</SelectItem>
                <SelectItem value="hoog">Hoog</SelectItem>
                <SelectItem value="normaal">Normaal</SelectItem>
                <SelectItem value="laag">Laag</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase font-bold">Status</Label>
            <Select value={form.status} onValueChange={(v) => setF("status", v)}>
              <SelectTrigger data-testid="call-log-status" className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(STATUS_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        {/* Samenvatting + actie */}
        <div>
          <Label className="text-[11px] uppercase font-bold">Korte samenvatting</Label>
          <Textarea data-testid="call-log-summary" rows={3} value={form.summary} onChange={(e) => setF("summary", e.target.value)} className="rounded-xl mt-1" />
        </div>
        <label className="flex items-center gap-2 cursor-pointer text-sm">
          <Checkbox data-testid="call-log-action-required" checked={form.action_required} onCheckedChange={(v) => setF("action_required", Boolean(v))} />
          <span><strong>Actie nodig</strong> — markeert deze registratie als follow-up vereist</span>
        </label>

        {/* Koppeling — menselijke zoek-en-selecteer (sprint jun 2026) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 bg-gray-50 border border-gray-200">
          <div>
            <Label className="text-[11px] uppercase font-bold">Koppel aan</Label>
            <Select value={form.link_type || "none"} onValueChange={(v) => setForm((cur) => ({
              ...cur, link_type: v === "none" ? "" : v, link_id: "", link_label: "",
            }))}>
              <SelectTrigger data-testid="call-log-link-type" className="rounded-xl mt-1"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Geen koppeling —</SelectItem>
                <SelectItem value="pickup">Pickup & Delivery</SelectItem>
                <SelectItem value="klant">Klant</SelectItem>
                <SelectItem value="zending">Zending</SelectItem>
                <SelectItem value="offerte">Offerte</SelectItem>
                <SelectItem value="factuur">Factuur</SelectItem>
                <SelectItem value="webshop_order">Webshoporder</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <LinkTargetPicker
            linkType={form.link_type}
            linkId={form.link_id}
            linkLabel={form.link_label}
            onChange={(patch) => setForm((cur) => ({ ...cur, ...patch }))}
            testIdPrefix="call-log-link"
          />
        </div>

        {/* Reminder direct vanuit call-log */}
        <div className="p-3 bg-amber-50 border border-amber-200 space-y-2">
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <Checkbox data-testid="call-log-add-reminder" checked={form.add_reminder} onCheckedChange={(v) => {
              setF("add_reminder", Boolean(v));
              // Veilige default: morgen, 09:00
              if (v && !form.reminder_date) setF("reminder_date", tomorrowIso());
            }} />
            <span className="font-bold text-amber-900">+ Reminder aanmaken</span>
            <span className="text-xs text-amber-700">(opvolging via Reminders-tab)</span>
          </label>
          {form.add_reminder && (
            <div className="space-y-2 pl-6" data-testid="call-log-reminder-fields">
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-[10px] uppercase font-bold">Datum *</Label>
                  <DatePickerField value={form.reminder_date} onChange={(d) => setF("reminder_date", d)} testId="call-log-reminder-date" placeholder="Datum" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase font-bold">Tijd</Label>
                  <Input type="time" data-testid="call-log-reminder-time" value={form.reminder_time} onChange={(e) => setF("reminder_time", e.target.value)} className="rounded-xl mt-1" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase font-bold">Prioriteit</Label>
                  <Select value={form.reminder_priority} onValueChange={(v) => setF("reminder_priority", v)}>
                    <SelectTrigger data-testid="call-log-reminder-priority" className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="urgent">Urgent</SelectItem>
                      <SelectItem value="hoog">Hoog</SelectItem>
                      <SelectItem value="normaal">Normaal</SelectItem>
                      <SelectItem value="laag">Laag</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-[10px] uppercase font-bold">Extra reminder-tekst (optioneel)</Label>
                <Textarea data-testid="call-log-reminder-text" rows={2} value={form.reminder_text} onChange={(e) => setF("reminder_text", e.target.value)} className="rounded-xl mt-1" placeholder='Standaard: "Opvolging telefoonnotitie: [onderwerp] — [klantnaam]"' />
              </div>
            </div>
          )}
        </div>

        {/* Interne notitie */}
        <div>
          <Label className="text-[11px] uppercase font-bold">Interne notitie</Label>
          <Textarea data-testid="call-log-internal-note" rows={2} value={form.internal_note} onChange={(e) => setF("internal_note", e.target.value)} className="rounded-xl mt-1" placeholder="Alleen zichtbaar voor medewerkers" />
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t border-gray-200 mt-4">
          <Button variant="outline" onClick={onClose} className="rounded-xl text-xs">Annuleren</Button>
          <Button onClick={submit} disabled={submitting} className="bg-[#082c50] hover:bg-[#06223f] text-white rounded-xl text-xs font-chivo font-bold uppercase" data-testid="call-log-save">
            {submitting ? "Bezig..." : "Registratie opslaan"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// Detail dialog — dossier-modal met secties + bewerken + vervolgnotities
// ============================================================
function CallLogDetailDialog({ cl: clInitial, onClose, onUpdated }) {
  const [cl, setCl] = useState(clInitial);
  const [mode, setMode] = useState("view"); // "view" | "edit" | "follow_up"
  const [saving, setSaving] = useState(false);

  // Edit form state — alleen geinitialiseerd als mode === edit
  const [editForm, setEditForm] = useState({
    subject: clInitial.subject || "",
    category: clInitial.category || "algemene_vraag",
    priority: clInitial.priority || "normaal",
    status: clInitial.status || "open",
    summary: clInitial.summary || "",
    internal_note: clInitial.internal_note || "",
    action_required: !!clInitial.action_required,
    link_type: clInitial.link_type || "",
    link_id: clInitial.link_id || "",
    link_label: clInitial.link_label || "",
  });
  // Sprint jun 2026 — direct een Pickup/Delivery plannen vanuit de registratie
  const [planOpen, setPlanOpen] = useState(false);

  // Follow-up state
  const [fuNote, setFuNote] = useState("");
  const [fuStatus, setFuStatus] = useState("");
  const [fuAddReminder, setFuAddReminder] = useState(false);
  const [fuReminder, setFuReminder] = useState({ date: "", time_of_day: "", priority: "normaal" });

  const refreshCl = async () => {
    try {
      const r = await adminApi.get(`/admin/call-logs/${cl.id}`);
      setCl(r.data);
    } catch {/* ignore */}
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const payload = {
        subject: editForm.subject.trim(),
        category: editForm.category,
        priority: editForm.priority,
        status: editForm.status,
        summary: editForm.summary.trim() || null,
        internal_note: editForm.internal_note.trim() || null,
        action_required: editForm.action_required,
      };
      // Koppeling alleen meesturen als die daadwerkelijk is gewijzigd; de
      // backend leidt het menselijke label zelf af en valideert fail-closed.
      const linkTypeChanged = (editForm.link_type || "") !== (cl.link_type || "");
      const linkIdChanged = (editForm.link_id || "") !== (cl.link_id || "");
      if (linkTypeChanged || linkIdChanged) {
        if (editForm.link_type && !editForm.link_id) {
          toast.error("Selecteer eerst het record waaraan je wilt koppelen.");
          setSaving(false);
          return;
        }
        payload.link_type = editForm.link_type || null;
        payload.link_id = editForm.link_id || null;
      }
      await adminApi.patch(`/admin/call-logs/${cl.id}`, payload);
      toast.success("Registratie bijgewerkt");
      await refreshCl();
      setMode("view");
      onUpdated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Opslaan mislukt");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveFollowUp = async () => {
    if (!fuNote.trim()) {
      toast.error("Vervolgnotitie mag niet leeg zijn");
      return;
    }
    if (fuAddReminder && !fuReminder.date) {
      toast.error("Reminder-datum is verplicht");
      return;
    }
    setSaving(true);
    try {
      const payload = { note: fuNote.trim() };
      if (fuStatus && fuStatus !== cl.status) payload.status_after = fuStatus;
      if (fuAddReminder) {
        payload.reminder = {
          date: fuReminder.date,
          time_of_day: fuReminder.time_of_day || null,
          priority: fuReminder.priority || "normaal",
        };
      }
      await adminApi.post(`/admin/call-logs/${cl.id}/follow-up`, payload);
      toast.success("Vervolgnotitie toegevoegd");
      await refreshCl();
      setFuNote("");
      setFuStatus("");
      setFuAddReminder(false);
      setFuReminder({ date: "", time_of_day: "", priority: "normaal" });
      setMode("view");
      onUpdated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Opslaan mislukt");
    } finally {
      setSaving(false);
    }
  };

  const quickStatusChange = async (newStatus) => {
    if (newStatus === cl.status) return;
    setSaving(true);
    try {
      await adminApi.post(`/admin/call-logs/${cl.id}/status`, { status: newStatus });
      toast.success("Status bijgewerkt");
      await refreshCl();
      onUpdated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Opslaan mislukt");
    } finally {
      setSaving(false);
    }
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "short" }) : "—";
  const followUpNotes = Array.isArray(cl.follow_up_notes) ? cl.follow_up_notes : [];

  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="rounded-xl max-w-3xl max-h-[92vh] overflow-y-auto"
        data-testid="call-log-detail"
      >
        <DialogHeader>
          <DialogTitle className="font-chivo font-black text-xl text-[#082c50] flex items-center gap-2 flex-wrap">
            {cl.subject}
            {cl.action_required && (
              <span className="text-[10px] uppercase font-bold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5">⚡ Actie nodig</span>
            )}
          </DialogTitle>
          <DialogDescription>
            {CATEGORY_LABELS[cl.category] || cl.category} · {cl.customer_name} · aangemaakt {fmtDate(cl.created_at)}
            {cl.updated_at && cl.updated_at !== cl.created_at && (
              <> · laatst gewijzigd {fmtDate(cl.updated_at)}</>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Actie-balk */}
        <div className="flex flex-wrap items-center gap-2 pb-3 border-b border-gray-200">
          {mode === "view" && (
            <>
              <Button size="sm" variant="outline" onClick={() => setMode("edit")} className="rounded-xl text-xs" data-testid="call-log-edit-btn">
                Bewerken
              </Button>
              <Button size="sm" onClick={() => setMode("follow_up")} className="bg-[#082c50] text-white rounded-xl text-xs" data-testid="call-log-follow-up-btn">
                + Vervolgnotitie
              </Button>
              <Button size="sm" onClick={() => setPlanOpen(true)}
                      className="bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-xl text-xs"
                      data-testid="call-log-plan-btn">
                <CalendarClock className="w-3.5 h-3.5 mr-1" /> Direct plannen
              </Button>
              <div className="flex items-center gap-2 ml-auto">
                <Label className="text-xs">Snel:</Label>
                <Select value={cl.status} onValueChange={quickStatusChange}>
                  <SelectTrigger className="w-44 rounded-xl text-xs h-9" data-testid="call-log-detail-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(STATUS_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </>
          )}
          {(mode === "edit" || mode === "follow_up") && (
            <Button size="sm" variant="ghost" onClick={() => setMode("view")} className="rounded-xl text-xs">
              ← Terug naar overzicht
            </Button>
          )}
        </div>

        {mode === "view" && (
          <ViewSections cl={cl} fmtDate={fmtDate} followUpNotes={followUpNotes} />
        )}

        {mode === "edit" && (
          <EditForm form={editForm} setForm={setEditForm} onCancel={() => setMode("view")} onSave={handleSaveEdit} saving={saving} />
        )}

        {mode === "follow_up" && (
          <FollowUpForm
            note={fuNote} setNote={setFuNote}
            status={fuStatus} setStatus={setFuStatus}
            currentStatus={cl.status}
            addReminder={fuAddReminder} setAddReminder={setFuAddReminder}
            reminder={fuReminder} setReminder={setFuReminder}
            onCancel={() => setMode("view")}
            onSave={handleSaveFollowUp}
            saving={saving}
          />
        )}

        {planOpen && (
          <PlanAppointmentDialog
            cl={cl}
            onClose={() => setPlanOpen(false)}
            onPlanned={async () => { await refreshCl(); onUpdated(); }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function ViewSections({ cl, fmtDate, followUpNotes }) {
  return (
    <div className="space-y-4">
      {/* Klantgegevens */}
      <section data-testid="cl-section-klant">
        <SectionTitle>Klantgegevens</SectionTitle>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <KV label="Klantnaam" value={cl.customer_name} />
          <KV label="Telefoon" value={cl.phone} />
          <KV label="E-mail" value={cl.email} />
          <KV label="Bedrijf" value={cl.company_name} />
        </div>
      </section>

      {/* Gespreksinformatie */}
      <section data-testid="cl-section-gesprek">
        <SectionTitle>Gespreksinformatie</SectionTitle>
        <div className="grid grid-cols-3 gap-2 text-sm">
          <KV label="Categorie" value={CATEGORY_LABELS[cl.category] || cl.category} />
          <KV label="Prioriteit" value={cl.priority} />
          <KV label="Status" value={STATUS_LABELS[cl.status] || cl.status} />
        </div>
      </section>

      {/* Samenvatting */}
      {cl.summary && (
        <section data-testid="cl-section-samenvatting">
          <SectionTitle>Samenvatting</SectionTitle>
          <div className="p-3 bg-gray-50 border border-gray-200 text-sm whitespace-pre-wrap">{cl.summary}</div>
        </section>
      )}

      {/* Interne notitie */}
      {cl.internal_note && (
        <section data-testid="cl-section-interne-notitie">
          <SectionTitle>Interne notitie</SectionTitle>
          <div className="p-3 bg-amber-50 border border-amber-200 text-xs italic whitespace-pre-wrap">{cl.internal_note}</div>
        </section>
      )}

      {/* Tijdlijn */}
      <section data-testid="cl-section-tijdlijn">
        <SectionTitle>Vervolgnotities & tijdlijn ({followUpNotes.length})</SectionTitle>
        {followUpNotes.length === 0 ? (
          <div className="text-xs text-gray-500 italic">Nog geen vervolgnotities. Klik &quot;+ Vervolgnotitie&quot; bovenaan om er een toe te voegen.</div>
        ) : (
          <ol className="space-y-2">
            {[...followUpNotes].reverse().map((fu) => (
              <li key={fu.id} className="border-l-2 border-[#082c50] pl-3 py-1" data-testid={`cl-followup-${fu.id}`}>
                <div className="text-xs font-bold text-[#082c50]">
                  {fmtDate(fu.created_at)} — {fu.created_by_name || fu.created_by_email}
                </div>
                <div className="text-sm mt-0.5 whitespace-pre-wrap">{fu.note}</div>
                <div className="flex gap-2 flex-wrap mt-1">
                  {fu.status_after && (
                    <span className="text-[10px] uppercase font-bold bg-blue-50 text-blue-700 border border-blue-200 px-1.5 py-0.5">
                      Status: {STATUS_LABELS[fu.status_before] || fu.status_before} → {STATUS_LABELS[fu.status_after] || fu.status_after}
                    </span>
                  )}
                  {fu.reminder_id && (
                    <span className="text-[10px] uppercase font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5">
                      <Clock className="w-2.5 h-2.5 inline" /> Reminder aangemaakt
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>

      {/* Reminders */}
      {cl.reminder_id && (
        <section data-testid="cl-section-reminders">
          <SectionTitle>Reminders</SectionTitle>
          <div className="p-3 bg-emerald-50 border border-emerald-200 text-xs text-emerald-800">
            <CheckCircle2 className="w-3 h-3 inline mr-1" /> Gekoppelde reminder(s) zichtbaar in Reminders-tab.
          </div>
        </section>
      )}

      {/* Gekoppeld record — menselijk leesbaar (sprint jun 2026) */}
      {cl.link_type && (
        <section data-testid="cl-section-linked">
          <SectionTitle>Gekoppeld record</SectionTitle>
          <div className="p-3 bg-blue-50 border border-blue-200 text-sm flex items-start gap-2">
            <LinkIcon className="w-4 h-4 text-[#1a5193] mt-0.5 shrink-0" />
            <div className="min-w-0">
              <div className="text-[10px] uppercase font-bold text-[#1a5193]">
                {LINK_TYPE_LABELS[cl.link_type] || cl.link_type}
              </div>
              <div className="font-bold text-[#082c50] break-words" data-testid="cl-linked-label">
                {cl.link_label || "Gekoppeld record"}
              </div>
              {cl.link_type === "pickup" && cl.link_id && (
                <a href={`/admin?section=pickup&pickup_id=${cl.link_id}`}
                   className="text-[10px] uppercase font-bold text-[#0055FF] hover:underline"
                   data-testid="cl-linked-open">
                  Afspraak openen
                </a>
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function EditForm({ form, setForm, onCancel, onSave, saving }) {
  const setF = (k, v) => setForm((cur) => ({ ...cur, [k]: v }));
  return (
    <div className="space-y-3" data-testid="cl-edit-form">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <Label className="text-[11px] uppercase font-bold">Onderwerp</Label>
          <Input value={form.subject} onChange={(e) => setF("subject", e.target.value)} className="rounded-xl mt-1" data-testid="cl-edit-subject" />
        </div>
        <div>
          <Label className="text-[11px] uppercase font-bold">Categorie</Label>
          <Select value={form.category} onValueChange={(v) => setF("category", v)}>
            <SelectTrigger className="rounded-xl mt-1" data-testid="cl-edit-category"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(CATEGORY_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase font-bold">Prioriteit</Label>
          <Select value={form.priority} onValueChange={(v) => setF("priority", v)}>
            <SelectTrigger className="rounded-xl mt-1" data-testid="cl-edit-priority"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="urgent">Urgent</SelectItem>
              <SelectItem value="hoog">Hoog</SelectItem>
              <SelectItem value="normaal">Normaal</SelectItem>
              <SelectItem value="laag">Laag</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase font-bold">Status</Label>
          <Select value={form.status} onValueChange={(v) => setF("status", v)}>
            <SelectTrigger className="rounded-xl mt-1" data-testid="cl-edit-status"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(STATUS_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <label className="flex items-center gap-2 text-xs mt-6">
          <input type="checkbox" checked={form.action_required} onChange={(e) => setF("action_required", e.target.checked)} data-testid="cl-edit-action-required" />
          Actie nodig
        </label>
      </div>
      <div>
        <Label className="text-[11px] uppercase font-bold">Samenvatting</Label>
        <Textarea rows={3} value={form.summary} onChange={(e) => setF("summary", e.target.value)} className="rounded-xl mt-1" data-testid="cl-edit-summary" />
      </div>
      <div>
        <Label className="text-[11px] uppercase font-bold">Interne notitie</Label>
        <Textarea rows={2} value={form.internal_note} onChange={(e) => setF("internal_note", e.target.value)} className="rounded-xl mt-1" data-testid="cl-edit-internal-note" placeholder="Alleen zichtbaar voor medewerkers" />
      </div>
      {/* Koppeling wijzigen — menselijk zoeken/selecteren (sprint jun 2026) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 bg-gray-50 border border-gray-200">
        <div>
          <Label className="text-[11px] uppercase font-bold">Koppel aan</Label>
          <Select value={form.link_type || "none"} onValueChange={(v) => setForm((cur) => ({
            ...cur, link_type: v === "none" ? "" : v, link_id: "", link_label: "",
          }))}>
            <SelectTrigger className="rounded-xl mt-1" data-testid="cl-edit-link-type"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— Geen koppeling —</SelectItem>
              <SelectItem value="pickup">Pickup & Delivery</SelectItem>
              <SelectItem value="klant">Klant</SelectItem>
              <SelectItem value="zending">Zending</SelectItem>
              <SelectItem value="offerte">Offerte</SelectItem>
              <SelectItem value="factuur">Factuur</SelectItem>
              <SelectItem value="webshop_order">Webshoporder</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <LinkTargetPicker
          linkType={form.link_type}
          linkId={form.link_id}
          linkLabel={form.link_label}
          onChange={(patch) => setForm((cur) => ({ ...cur, ...patch }))}
          testIdPrefix="cl-edit-link"
        />
      </div>
      <div className="flex justify-end gap-2 pt-3 border-t border-gray-200">
        <Button variant="outline" onClick={onCancel} className="rounded-xl text-xs">Annuleren</Button>
        <Button onClick={onSave} disabled={saving} className="bg-[#082c50] text-white rounded-xl text-xs font-chivo font-bold uppercase" data-testid="cl-edit-save">
          {saving ? "Bezig..." : "Wijzigingen opslaan"}
        </Button>
      </div>
    </div>
  );
}

function FollowUpForm({ note, setNote, status, setStatus, currentStatus, addReminder, setAddReminder, reminder, setReminder, onCancel, onSave, saving }) {
  return (
    <div className="space-y-3" data-testid="cl-followup-form">
      <div>
        <Label className="text-[11px] uppercase font-bold">Vervolgnotitie *</Label>
        <Textarea
          rows={4}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Bv. Klant opnieuw gesproken. Curaçao vestiging geeft aan dat zending nog niet is vrijgegeven. Klant wil morgen teruggebeld worden."
          className="rounded-xl mt-1"
          data-testid="cl-followup-note"
        />
      </div>
      <div>
        <Label className="text-[11px] uppercase font-bold">Status wijzigen (optioneel)</Label>
        <Select value={status || "_none"} onValueChange={(v) => setStatus(v === "_none" ? "" : v)}>
          <SelectTrigger className="rounded-xl mt-1" data-testid="cl-followup-status"><SelectValue placeholder="Status onveranderd" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_none">Niet wijzigen (huidig: {STATUS_LABELS[currentStatus] || currentStatus})</SelectItem>
            {Object.entries(STATUS_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <input type="checkbox" checked={addReminder} onChange={(e) => setAddReminder(e.target.checked)} data-testid="cl-followup-add-reminder" />
          <span className="font-bold">Reminder aanmaken</span>
        </label>
        {addReminder && (
          <div className="mt-2 grid grid-cols-3 gap-2 p-3 bg-amber-50 border border-amber-200 rounded">
            <div>
              <Label className="text-[10px] uppercase font-bold">Datum *</Label>
              <Input type="date" value={reminder.date} onChange={(e) => setReminder({ ...reminder, date: e.target.value })} className="rounded-xl mt-1 text-xs h-9" data-testid="cl-followup-reminder-date" />
            </div>
            <div>
              <Label className="text-[10px] uppercase font-bold">Tijd</Label>
              <Input placeholder="HH:MM" value={reminder.time_of_day} onChange={(e) => setReminder({ ...reminder, time_of_day: e.target.value })} className="rounded-xl mt-1 text-xs h-9" data-testid="cl-followup-reminder-time" />
            </div>
            <div>
              <Label className="text-[10px] uppercase font-bold">Prioriteit</Label>
              <Select value={reminder.priority} onValueChange={(v) => setReminder({ ...reminder, priority: v })}>
                <SelectTrigger className="rounded-xl mt-1 text-xs h-9" data-testid="cl-followup-reminder-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="hoog">Hoog</SelectItem>
                  <SelectItem value="normaal">Normaal</SelectItem>
                  <SelectItem value="laag">Laag</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </div>
      <div className="flex justify-end gap-2 pt-3 border-t border-gray-200">
        <Button variant="outline" onClick={onCancel} className="rounded-xl text-xs">Annuleren</Button>
        <Button onClick={onSave} disabled={saving} className="bg-[#082c50] text-white rounded-xl text-xs font-chivo font-bold uppercase" data-testid="cl-followup-save">
          {saving ? "Bezig..." : "Vervolgnotitie opslaan"}
        </Button>
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return <h3 className="text-[10px] uppercase font-bold tracking-wide text-gray-700 mb-1.5">{children}</h3>;
}

// ============================================================
// Koppeldoel kiezen — menselijk zoeken/selecteren i.p.v. UUID plakken
// ============================================================
function LinkTargetPicker({ linkType, linkId, linkLabel, onChange, testIdPrefix }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  if (!linkType) return <div className="hidden md:block" />;

  const entityType = LINK_ENTITY_TYPES[linkType];
  if (!entityType) {
    // Webshoporder heeft (nog) geen zoekadapter — dan blijft handmatige
    // referentie de enige optie, maar wél expliciet gelabeld.
    return (
      <div>
        <Label className="text-[11px] uppercase font-bold">Ordernummer / referentie</Label>
        <Input
          data-testid={`${testIdPrefix}-manual-id`}
          value={linkId || ""}
          onChange={(e) => onChange({ link_id: e.target.value, link_label: "" })}
          placeholder="Bijv. 5999-12345"
          className="rounded-xl mt-1 text-xs"
        />
      </div>
    );
  }

  return (
    <div>
      <Label className="text-[11px] uppercase font-bold">
        {LINK_TYPE_LABELS[linkType]} zoeken
      </Label>
      {linkId ? (
        <div
          className="mt-1 flex items-start gap-2 border border-emerald-200 bg-emerald-50 px-2 py-1.5"
          data-testid={`${testIdPrefix}-selected`}
        >
          <LinkIcon className="w-3.5 h-3.5 text-emerald-700 mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-xs font-bold text-[#082c50] break-words">
              {linkLabel || "Gekoppeld record"}
            </div>
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              className="text-[10px] uppercase font-bold text-[#0055FF] hover:underline mt-0.5"
              data-testid={`${testIdPrefix}-change`}
            >
              Andere kiezen
            </button>
          </div>
          <button
            type="button"
            onClick={() => onChange({ link_id: "", link_label: "" })}
            className="p-0.5 text-gray-400 hover:text-red-600 shrink-0"
            aria-label="Koppeling wissen"
            data-testid={`${testIdPrefix}-clear`}
          >
            <XIcon className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <Button
          type="button" variant="outline"
          onClick={() => setPickerOpen(true)}
          className="rounded-xl mt-1 w-full justify-start text-xs h-10"
          data-testid={`${testIdPrefix}-open-picker`}
        >
          <Search className="w-3.5 h-3.5 mr-1.5" />
          Zoek op naam, plaats, nummer of datum…
        </Button>
      )}
      <EnterpriseEntityPicker
        entityType={entityType}
        mode="dialog"
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        testIdPrefix={`${testIdPrefix}-picker`}
        onSelect={(item) => onChange({
          link_id: item.id,
          link_label: [item.primary_text, item.secondary_text].filter(Boolean).join(" · "),
        })}
      />
    </div>
  );
}

// ============================================================
// Direct plannen — Pickup / Delivery vanuit de telefoonregistratie
// ============================================================
function PlanAppointmentDialog({ cl, onClose, onPlanned }) {
  const [kind, setKind] = useState(
    cl.category === "pickup_aanvraag" ? "pickup" : "delivery",
  );
  const [pickupDate, setPickupDate] = useState("");
  const [pickupTime, setPickupTime] = useState("");
  const [deliveryDate, setDeliveryDate] = useState("");
  const [deliveryTime, setDeliveryTime] = useState("");
  const [instructions, setInstructions] = useState("");
  const [shipment, setShipment] = useState(null);
  const [shipmentPickerOpen, setShipmentPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const wantsPickup = kind === "pickup" || kind === "both";
  const wantsDelivery = kind === "delivery" || kind === "both";

  const submit = async () => {
    setSaving(true);
    try {
      const r = await adminApi.post(`/admin/call-logs/${cl.id}/plan-appointment`, {
        kind,
        pickup_date: wantsPickup ? (pickupDate || null) : null,
        pickup_time: wantsPickup ? (pickupTime || null) : null,
        delivery_date: wantsDelivery ? (deliveryDate || null) : null,
        delivery_time: wantsDelivery ? (deliveryTime || null) : null,
        shipment_id: shipment?.id || null,
        instructions: instructions.trim() || null,
      });
      toast.success("Afspraak gepland en gekoppeld aan deze registratie");
      onPlanned?.(r.data);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent className="rounded-xl max-w-xl max-h-[92vh] overflow-y-auto" data-testid="call-log-plan-dialog">
        <DialogHeader>
          <DialogTitle className="font-chivo font-black text-xl text-[#082c50] flex items-center gap-2">
            <CalendarClock className="w-5 h-5 text-[#0055FF]" /> Direct plannen
          </DialogTitle>
          <DialogDescription>
            De bekende klant- en contactgegevens uit deze registratie worden
            overgenomen. De afspraak wordt automatisch aan deze
            telefoonregistratie gekoppeld.
          </DialogDescription>
        </DialogHeader>

        <div className="bg-gray-50 border border-gray-200 p-2 text-xs" data-testid="call-log-plan-prefill">
          <span className="font-bold text-[#082c50]">{cl.customer_name || "—"}</span>
          {cl.phone && <> · {cl.phone}</>}
          {cl.email && <> · {cl.email}</>}
          {cl.city && <> · {cl.city}</>}
        </div>

        {cl.link_type && cl.link_id && (
          <div className="bg-amber-50 border border-amber-200 p-2 text-xs text-amber-900"
               data-testid="call-log-plan-relink-warning">
            Deze registratie is nu gekoppeld aan{" "}
            <strong>{cl.link_label || LINK_TYPE_LABELS[cl.link_type] || cl.link_type}</strong>.
            Na het plannen verwijst de koppeling naar de nieuwe afspraak.
          </div>
        )}

        <div>
          <Label className="text-[11px] uppercase font-bold">Soort afspraak</Label>
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger className="rounded-xl mt-1" data-testid="call-log-plan-kind"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pickup">Pickup (ophalen)</SelectItem>
              <SelectItem value="delivery">Delivery (afleveren)</SelectItem>
              <SelectItem value="both">Pickup + Delivery</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {wantsPickup && (
          <div className="grid grid-cols-2 gap-2" data-testid="call-log-plan-pickup-fields">
            <div>
              <Label className="text-[10px] uppercase font-bold">Ophaaldatum</Label>
              <DatePickerField value={pickupDate} onChange={setPickupDate} testId="call-log-plan-pickup-date" placeholder="Datum" />
            </div>
            <div>
              <Label className="text-[10px] uppercase font-bold">Ophaaltijd</Label>
              <Input type="time" value={pickupTime} onChange={(e) => setPickupTime(e.target.value)} className="rounded-xl mt-1" data-testid="call-log-plan-pickup-time" />
            </div>
          </div>
        )}

        {wantsDelivery && (
          <div className="grid grid-cols-2 gap-2" data-testid="call-log-plan-delivery-fields">
            <div>
              <Label className="text-[10px] uppercase font-bold">Afleverdatum</Label>
              <DatePickerField value={deliveryDate} onChange={setDeliveryDate} testId="call-log-plan-delivery-date" placeholder="Datum" />
            </div>
            <div>
              <Label className="text-[10px] uppercase font-bold">Aflevertijd</Label>
              <Input type="time" value={deliveryTime} onChange={(e) => setDeliveryTime(e.target.value)} className="rounded-xl mt-1" data-testid="call-log-plan-delivery-time" />
            </div>
          </div>
        )}

        <div>
          <Label className="text-[11px] uppercase font-bold">Zending koppelen (optioneel)</Label>
          {shipment ? (
            <div className="mt-1 flex items-start gap-2 border border-emerald-200 bg-emerald-50 px-2 py-1.5" data-testid="call-log-plan-shipment-selected">
              <LinkIcon className="w-3.5 h-3.5 text-emerald-700 mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1 text-xs font-bold text-[#082c50] break-words">
                {[shipment.primary_text, shipment.secondary_text].filter(Boolean).join(" · ")}
              </div>
              <button type="button" onClick={() => setShipment(null)}
                      className="p-0.5 text-gray-400 hover:text-red-600 shrink-0"
                      aria-label="Zending ontkoppelen"
                      data-testid="call-log-plan-shipment-clear">
                <XIcon className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <Button type="button" variant="outline" onClick={() => setShipmentPickerOpen(true)}
                    className="rounded-xl mt-1 w-full justify-start text-xs h-10"
                    data-testid="call-log-plan-shipment-open">
              <Search className="w-3.5 h-3.5 mr-1.5" /> Zoek zending (W-nummer of ontvanger)
            </Button>
          )}
          <p className="text-[10px] text-gray-500 mt-1">
            Bij een gekoppelde zending wordt het bekende afleveradres van de
            ontvanger overgenomen.
          </p>
          <EnterpriseEntityPicker
            entityType="shipment" mode="dialog"
            open={shipmentPickerOpen} onOpenChange={setShipmentPickerOpen}
            testIdPrefix="call-log-plan-shipment-picker"
            onSelect={(item) => setShipment(item)}
          />
        </div>

        <div>
          <Label className="text-[11px] uppercase font-bold">Instructies voor de chauffeur</Label>
          <Textarea rows={2} value={instructions} onChange={(e) => setInstructions(e.target.value)}
                    className="rounded-xl mt-1" data-testid="call-log-plan-instructions"
                    placeholder="Bijv. bellen bij aankomst, achterom laden" />
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-gray-200">
          <Button variant="outline" onClick={onClose} disabled={saving} className="rounded-xl text-xs">Annuleren</Button>
          <Button onClick={submit} disabled={saving}
                  className="bg-[#082c50] hover:bg-[#06223f] text-white rounded-xl text-xs font-chivo font-bold uppercase"
                  data-testid="call-log-plan-save">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CalendarClock className="w-3.5 h-3.5 mr-1" />}
            Afspraak plannen
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function KV({ label, value }) {
  return (
    <div className="text-sm">
      <span className="text-[10px] uppercase font-bold text-gray-500">{label}</span>
      <div className="text-gray-900 break-words">{value || <span className="text-gray-400">—</span>}</div>
    </div>
  );
}

function Field({ label, testId, value, onChange }) {
  return (
    <div>
      <Label className="text-[11px] uppercase font-bold">{label}</Label>
      <Input data-testid={testId} value={value || ""} onChange={(e) => onChange(e.target.value)} className="rounded-xl mt-1" />
    </div>
  );
}
